import app from 'flarum/forum/app';
// ⚠️ 路径是 `flarum/forum/components/Notification`, **不是** Flarum 1.x 的
//    `flarum/components/Notification`。实测 (浏览器里 flarum.reg.get):
//      flarum/forum/components/Notification  → class, 可用
//      flarum/components/Notification         → undefined
//    写错的后果特别隐蔽: undefined 当基类会在**模块求值期**抛
//    "Class extends value undefined is not a constructor or null",
//    整个扩展 bundle 当场挂掉 —— 路由、铃铛组件、全部 tab 一起消失,
//    而 webpack 构建期完全无报错 (externals 把它换成运行期查表)。
import Notification from 'flarum/forum/components/Notification';

/**
 * v0.17.31 — 约课通知的铃铛渲染组件 (辉哥 10/14 拍板)
 *
 * 4 个通知类型共用一个组件, 按 `notification.contentType()` 分支。
 * 合并而不是拆 4 个的理由: 四条文案的**结构完全一样**, 只有主句不同 ——
 * 「{教练} 为 {学员} 约了课」/「{教练} 改了 {学员} 的课的时间」/「{学员} 的课被取消了」/「{学员} 的课已恢复」,
 * 尾部都是同一段「· M月D日 HH:mm–HH:mm · 校区 教室」。拆开就是四份复制粘贴的格式化代码。
 *
 * ## 用 contentType() 而不是 type()
 *   Flarum 2.0 的 Notification 模型没有 type() 方法。`contentType()` 读的
 *   `data.attributes.contentType` 才是通知类型 —— 后端 NotificationResource 里
 *   `Schema\Str::make('contentType')->property('type')` 把它映射自 notifications.type 那一列。
 *   `app.notificationComponents[...]` 也是拿它做 key (ForumApplication + NotificationType.tsx),
 *   所以 4 个注册名必须跟 Blueprint::getType() 返回的字符串逐字一致。
 *
 * ## 后缀的拼装
 *   主句后面统一追加 `· {M月D日 HH:mm}–{HH:mm} · {校区名} {教室名}`。
 *   校区/教室任一为空时整段省略 (不是留一个空括号), 空壳比省略更丑。
 *
 * ## 时间按浏览器本地时区解析
 *   PHP 侧透传的 startAt/endAt 是 'Y-m-d H:i:s' 的**墙上时间字符串**
 *   (见 src/Notification/Blueprint/AbstractLessonBlueprint 的时间约定段)。
 *   `new Date('2026-10-14 09:00:00')` 在浏览器里按本地时区解析 ——
 *   跟 LessonsTab / CalendarTab 显示课次时间是同一条路径, 所以通知和课表必然对得上。
 *
 * ## 点击跳转
 *   一律去 `/scheduler/lessons` (预约管理 tab)。
 *   ⚠️ 路由名两端不对称: PHP 侧 `$request->getAttribute('routeName')` 是 `scheduler.lessons`,
 *      而 JS 侧 `app.routes` 的 key 要看扩展有没有被加前缀。所以先试无前缀、
 *      再试带 `ziiven-ziven-scheduler.` 前缀的写法; 两个都不存在就直接用字面量 URL ——
 *      `app.route()` 遇到不存在的名字是**抛异常**的, 不能拿它当探测手段 (会炸铃铛)。
 */
export default class SchedulerLessonNotification extends Notification {
  icon() {
    switch (this.type()) {
      // v0.18.0 (辉哥 10/18 拍板) — 课评通知, 图标 fa-star
      case 'schedulerLessonReviewed':
        return 'fas fa-star';
      case 'schedulerLessonRescheduled':
        return 'fas fa-clock';
      case 'schedulerLessonCancelled':
        return 'fas fa-ban';
      case 'schedulerLessonRestored':
        return 'fas fa-undo';
      default:
        return 'fas fa-calendar-check';
    }
  }
  href() {
    const name = ['scheduler.lessons', 'ziiven-ziven-scheduler.scheduler.lessons'].find(routeName => !!app.routes[routeName]);
    return name ? app.route(name, {}) : '/scheduler/lessons';
  }

  /**
   * v0.17.31a — 返回 **vnode 数组**, 不用模板字符串拼。
   *
   * 为什么不能拼: `app.translator.trans()` 在文案**含 {占位符}** 时返回的不是
   * 字符串, 而是 Mithril vnode 数组 (占位符被换成了 `<strong>` 之类的节点,
   * 见 core 的 DiscussionRenamedNotification.content() —— 它也是直接 return trans()
   * 的结果, 从来不拼字符串)。把它塞进 `` `${main} · ${suffix}` `` 会隐式触发
   * `Array.prototype.toString()`, 数组元素之间用**逗号** join, 于是实测渲染出:
   *     Kevin, 为 ,Ziven, 约了课 · 4月10日 19:00–20:00 · 默认校区 v067-教室A
   * 每两个 vnode 之间多一个逗号。
   *
   * 改法: 后缀作为**兄弟节点**返回。Mithril 自己会递归扁平化嵌套数组,
   * `[main, ' · ', suffix]` 里 main 本身是数组也没问题。
   * HeaderListItem 里是 `<span className="HeaderListItem-content">{content}</span>`,
   * content 直接当 child 渲染, 不做任何字符串化。
   */
  content() {
    const data = this.attrs.notification.content() || {};
    const when = formatWhen(data.startAt, data.endAt);
    const where = [data.schoolName, data.roomName].filter(Boolean).join(' ');
    const main = app.translator.trans(mainTextKey(this.type()), {
      coach: data.coachName || '?',
      student: data.studentName || '?',
      // v0.18.0 — 课评通知多一个星级占位符 (默认 5 星, 辉哥 10/18 追加规则)
      rating: data.rating || 5
    });
    const suffix = [when, where].filter(Boolean).join(' · ');
    return suffix ? [main, ' · ', suffix] : main;
  }
  excerpt() {
    return null;
  }
  type() {
    return this.attrs.notification.contentType();
  }
}

/** 每个 type 对应的主句文案 key (尾部的时间/地点由 content() 统一拼) */
function mainTextKey(type) {
  switch (type) {
    // v0.18.0 (辉哥 10/18 拍板) — 课评。
    //   ⚠️ 「{rating} 分」这个占位符**必须**走 translator 的 vnode 数组,
    //      不能在上面拼成字符串 (v0.17.31a 的踩坑史: 数组被 toString() 逗号 join)。
    case 'schedulerLessonReviewed':
      return 'ziven-scheduler.forum.notifications.lesson_reviewed_text';
    case 'schedulerLessonRescheduled':
      return 'ziven-scheduler.forum.notifications.lesson_rescheduled_text';
    case 'schedulerLessonCancelled':
      return 'ziven-scheduler.forum.notifications.lesson_cancelled_text';
    case 'schedulerLessonRestored':
      return 'ziven-scheduler.forum.notifications.lesson_restored_text';
    default:
      return 'ziven-scheduler.forum.notifications.lesson_booked_text';
  }
}

/** 「10月14日 09:00–10:00」 */
function formatWhen(startAt, endAt) {
  if (!startAt) return '';
  const start = new Date(startAt);
  if (isNaN(start.getTime())) return '';
  const startText = `${start.getMonth() + 1}月${start.getDate()}日 ${pad(start.getHours())}:${pad(start.getMinutes())}`;
  if (!endAt) return startText;
  const end = new Date(endAt);
  if (isNaN(end.getTime())) return startText;
  return `${startText}–${pad(end.getHours())}:${pad(end.getMinutes())}`;
}
function pad(n) {
  return n < 10 ? `0${n}` : String(n);
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/components/SchedulerLessonNotification', SchedulerLessonNotification);